import { html, type TemplateResult } from "lit";

export type TextSegment = { type: "text"; value: string } | { type: "link"; value: string; href: string };

const URL_REGEX = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+\.[^\s<>"']+)/gi;
const TRAILING_PUNCTUATION = /[.,)!?;:]+$/;

function stripTrailingPunctuation(url: string): { url: string; trailing: string } {
  const match = url.match(TRAILING_PUNCTUATION);
  if (!match) return { url, trailing: "" };

  const punctuation = match[0];
  const stripped = url.slice(0, -punctuation.length);

  if (stripped.includes("?") || stripped.includes("&")) {
    const lastPunct = url[url.length - 1];
    if (lastPunct === "?" && url.indexOf("?") === url.length - 1) {
      return { url: stripped, trailing: punctuation };
    }
    if (lastPunct !== "?" || url.indexOf("?") < url.length - 1) {
      return { url, trailing: "" };
    }
  }

  return { url: stripped, trailing: punctuation };
}

export function parseTextWithUrls(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(new RegExp(URL_REGEX))) {
    const matchIndex = match.index;
    const rawUrl = match[0];

    if (matchIndex > lastIndex) {
      segments.push({ type: "text", value: text.slice(lastIndex, matchIndex) });
    }

    const { url, trailing } = stripTrailingPunctuation(rawUrl);
    const href = url.match(/^https?:\/\//i) ? url : `https://${url}`;

    segments.push({ type: "link", value: url, href });

    lastIndex = matchIndex + rawUrl.length;

    if (trailing) {
      lastIndex = matchIndex + url.length;
    }
  }

  if (lastIndex < text.length) {
    segments.push({ type: "text", value: text.slice(lastIndex) });
  }

  if (segments.length === 0) {
    segments.push({ type: "text", value: text });
  }

  return segments;
}

export function linkifyText(text: string): TemplateResult | string {
  const segments = parseTextWithUrls(text);

  if (segments.length === 1 && segments[0]?.type === "text") {
    return text;
  }

  return html`${segments.map((segment) =>
    segment.type === "link"
      ? html`<a href=${segment.href} target="_blank" rel="noopener noreferrer">${segment.value}</a>`
      : segment.value,
  )}`;
}
